(() => {
  /**
   * KingKongUTV — Search within collection (Halo/Ella)
   * ✅ Self-healing (survives filter/sort/show-more DOM replacement)
   * ✅ Loads all collection pages via ?section_id=...&page=...
   * ✅ Filters all tiles (#main-collection-product-grid > li)
   * ✅ Hides pagination UI while search is active (prevents loading unfiltered items)
   * ✅ Searches BOTH:
   *    - Product Title
   *    - Shopify Product Type (exposed via Liquid as data-part-type="{{ product.type }}")
   * ✅ Tighter matching:
   *    - Multi-word query prefers exact PHRASE matches (e.g. "turbo kit")
   *    - If phrase matches exist, ONLY those show (tight)
   *    - Else fallback to AND token matching
   */

  const CONFIG = {
    gridSelector: '#main-collection-product-grid',
    itemSelector: '#main-collection-product-grid > li',

    wrapperSelector: '[data-collection-within-search]',
    inputSelector: '[data-within-search-input]',
    statusSelector: '[data-within-search-status]',
    paginateMetaSelector: '[data-within-pagination]',

    paginationWrapperSelector:
      '.pagination-wrapper, .halo-pagination, .pagination, [data-collection-pagination], .collection-pagination',

    showMoreSelector:
      'button[data-load-more], button.load-more, .pagination__button, .halo-infinite-scroll button, button.button--load-more, button[data-infinite-scroll]',

    minCharsToPreload: 2,
    debounceMs: 140,
    maxItems: 3000,

    // Tighter search behavior
    preferPhraseWhenPossible: true,
    andFallbackForMultiWord: true,
  };

  // Avoid double-binding
  if (window.__KK_WITHIN_SEARCH__) {
    window.__KK_WITHIN_SEARCH__.destroy();
  }

  function norm(str) {
    return (str || '')
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s-]+/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function singularize(t) {
    if (t.length > 3 && t.endsWith('s')) return t.slice(0, -1);
    return t;
  }

  function getCollectionTitleText(root) {
    const el =
      root.querySelector('.articleLookbook-title') ||
      root.querySelector('h1') ||
      root.querySelector('.page-title');
    return el?.textContent?.trim() || '';
  }

  function getSectionId(root) {
    return root.querySelector('.collection-default[data-section-id]')?.dataset.sectionId || null;
  }

  function getPaginationMeta(root) {
    const el = root.querySelector(CONFIG.paginateMetaSelector);
    const total = parseInt(el?.dataset.totalPages || '1', 10);
    const current = parseInt(el?.dataset.currentPage || '1', 10);
    return {
      totalPages: Number.isFinite(total) && total > 0 ? total : 1,
      currentPage: Number.isFinite(current) && current > 0 ? current : 1,
    };
  }

  function getKey(li) {
    const href = li.querySelector('a[href*="/products/"]')?.getAttribute('href');
    return href ? href.split('?')[0] : null;
  }

  function getTitle(li) {
    const sels = [
      '.product-title a',
      '.product-title',
      '.product-item-title a',
      '.product-item-title',
      '.card-title a',
      '.card-title',
      '.card__heading a',
      '.card__heading',
      '.card-information__text a',
      '.card-information__text',
      '.card__information a',
      '.card__information',
    ];

    for (const sel of sels) {
      const el = li.querySelector(sel);
      const txt = el?.textContent?.trim();
      if (txt) return norm(txt);
    }

    const links = li.querySelectorAll('a[href*="/products/"]');
    for (const a of links) {
      const aria = a.getAttribute('aria-label')?.trim();
      if (aria) return norm(aria);
      const titleAttr = a.getAttribute('title')?.trim();
      if (titleAttr) return norm(titleAttr);
    }

    const alt = li.querySelector('img[alt]')?.getAttribute('alt')?.trim();
    if (alt) return norm(alt);

    return norm(li.textContent || '');
  }

  // Shopify Product Type is exposed in Liquid as: data-part-type="{{ product.type }}"
  function getPartType(li) {
    return norm(li.getAttribute('data-part-type') || '');
  }

  class WithinSearchController {
    constructor(root) {
      this.root = root || document;

      this.wrapper = null;
      this.input = null;
      this.statusEl = null;
      this.grid = null;

      this.sectionId = null;
      this.collectionStopTokens = new Set();

      this.index = [];
      this.abortController = null;

      this.preloadStarted = false;
      this.preloadDone = false;
      this.isPreloading = false;

      this.moGrid = null;
      this.moRebind = null;

      this._debounceTimer = null;
      this._onInput = null;
      this._onKeydown = null;

      this.isInternalAppend = false;

      this.bind();
      this.watchForDomReplacement();
    }

    setStatus(msg) {
      if (this.statusEl) this.statusEl.textContent = msg || '';
    }

    togglePaginationUI(hide) {
      const el = this.root.querySelector(CONFIG.paginationWrapperSelector);
      if (!el) return;
      el.style.display = hide ? 'none' : '';
    }

    bindShowMoreHook() {
      const btn = this.root.querySelector(CONFIG.showMoreSelector);
      if (!btn || btn.dataset.withinSearchBound === '1') return;

      btn.dataset.withinSearchBound = '1';
      btn.addEventListener('click', () => {
        // Backup: re-filter after async append
        setTimeout(() => this.applyFilter(this.input?.value || ''), 250);
        setTimeout(() => this.applyFilter(this.input?.value || ''), 900);
      });
    }

    bind() {
      this.wrapper = this.root.querySelector(CONFIG.wrapperSelector);
      this.grid = this.root.querySelector(CONFIG.gridSelector);
      if (!this.wrapper || !this.grid) return;

      this.input = this.wrapper.querySelector(CONFIG.inputSelector);
      this.statusEl = this.wrapper.querySelector(CONFIG.statusSelector);
      if (!this.input) return;

      this.sectionId = getSectionId(this.root);

      // Remove collection title words from query (so "led whips" doesn't zero out results on LED Whips page)
      const collectionTitle = norm(getCollectionTitleText(this.root));
      this.collectionStopTokens = new Set(
        (collectionTitle ? collectionTitle.split(' ') : []).filter(Boolean).map(singularize)
      );

      this.buildIndex();
      this.bindShowMoreHook();

      this.unbindInputListeners();

      this._onInput = () => {
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => {
          this.preloadAllPages(this.input.value);
          this.applyFilter(this.input.value);
        }, CONFIG.debounceMs);
      };

      this._onKeydown = (e) => {
        if (e.key === 'Escape') {
          this.input.value = '';
          this.applyFilter('');
          this.setStatus('');
        }
      };

      this.input.addEventListener('input', this._onInput);
      this.input.addEventListener('keydown', this._onKeydown);

      this.disconnectGridObserver();
      this.moGrid = new MutationObserver(() => {
        this.buildIndex();
        this.bindShowMoreHook();

        // Theme changed DOM (filters/show more), reset preload flags (but not if WE appended)
        if (!this.isInternalAppend) {
          this.preloadStarted = false;
          this.preloadDone = false;
        }

        this.applyFilter(this.input.value);
      });
      this.moGrid.observe(this.grid, { childList: true });

      this.applyFilter(this.input.value);
    }

    unbindInputListeners() {
      if (this.input && this._onInput) this.input.removeEventListener('input', this._onInput);
      if (this.input && this._onKeydown) this.input.removeEventListener('keydown', this._onKeydown);
      this._onInput = null;
      this._onKeydown = null;
    }

    disconnectGridObserver() {
      if (this.moGrid) this.moGrid.disconnect();
      this.moGrid = null;
    }

    preprocessQuery(q) {
      const s = norm(q);
      if (!s) return { tokens: [], phrase: '' };

      let tokens = s.split(' ').filter(Boolean).map(singularize);

      // Remove collection title words
      tokens = tokens.filter((t) => !this.collectionStopTokens.has(t));

      if (!tokens.length) return { tokens: [], phrase: '' };

      return {
        tokens,
        phrase: tokens.length >= 2 ? tokens.join(' ') : '',
      };
    }

    buildIndex() {
      if (!this.grid) return;

      const items = Array.from(this.root.querySelectorAll(CONFIG.itemSelector));
      const seen = new Set();
      this.index = [];

      for (const li of items) {
        const key = getKey(li) || `node-${this.index.length}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const title = getTitle(li);
        const partType = getPartType(li);

        // Searchable text includes BOTH title + product type
        const haystack = norm(`${title} ${partType}`);

        this.index.push({
          el: li,
          key,
          title,
          partType,
          haystack,
        });
      }
    }

    computeMatches(tokens, phrase) {
      const matchedKeys = new Set();

      // Multi-word phrase priority (tight)
      const isMulti = tokens.length >= 2 && phrase;

      if (isMulti && CONFIG.preferPhraseWhenPossible) {
        let phraseCount = 0;
        for (const item of this.index) {
          if ((item.haystack || '').includes(phrase)) {
            matchedKeys.add(item.key);
            phraseCount++;
          }
        }
        if (phraseCount > 0) {
          return { matchedKeys, shownCount: phraseCount };
        }
      }

      // Single-word
      if (tokens.length === 1) {
        const t = tokens[0];
        let c = 0;
        for (const item of this.index) {
          if ((item.haystack || '').includes(t)) {
            matchedKeys.add(item.key);
            c++;
          }
        }
        return { matchedKeys, shownCount: c };
      }

      // Multi-word fallback: AND matching
      if (CONFIG.andFallbackForMultiWord) {
        let c = 0;
        for (const item of this.index) {
          const h = item.haystack || '';
          if (tokens.every((t) => h.includes(t))) {
            matchedKeys.add(item.key);
            c++;
          }
        }
        return { matchedKeys, shownCount: c };
      }

      // Last resort: OR
      let c = 0;
      for (const item of this.index) {
        const h = item.haystack || '';
        if (tokens.some((t) => h.includes(t))) {
          matchedKeys.add(item.key);
          c++;
        }
      }
      return { matchedKeys, shownCount: c };
    }

    applyFilter(q) {
      if (!this.wrapper || !this.grid || !this.input) return;

      const { tokens, phrase } = this.preprocessQuery(q);

      if (!this.index.length) this.buildIndex();

      // Hide pagination while search is active
      this.togglePaginationUI(tokens.length > 0);

      // If query collapses to empty -> show all
      if (!tokens.length) {
        for (const item of this.index) item.el.style.display = '';
        this.setStatus('');
        return;
      }

      const { matchedKeys, shownCount } = this.computeMatches(tokens, phrase);

      for (const item of this.index) {
        item.el.style.display = matchedKeys.has(item.key) ? '' : 'none';
      }

      // Status
      if (this.isPreloading) {
        this.setStatus(`${shownCount} shown (loading more…)`);
      } else {
        this.setStatus(`${shownCount} result${shownCount === 1 ? '' : 's'}`);
      }
    }

    async fetchPageLis(pageNum) {
      if (!this.sectionId) return [];

      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('page', String(pageNum));

      const res = await fetch(url.toString(), { signal: this.abortController.signal });
      if (!res.ok) throw new Error(`Failed page ${pageNum}: ${res.status}`);

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fetchedGrid = doc.querySelector(CONFIG.gridSelector);
      if (!fetchedGrid) return [];

      return Array.from(fetchedGrid.querySelectorAll(':scope > li'));
    }

    async preloadAllPages(queryStr) {
      if (this.preloadStarted) return;
      if (norm(queryStr).length < CONFIG.minCharsToPreload) return;

      this.preloadStarted = true;
      this.preloadDone = false;
      this.isPreloading = true;

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const { totalPages, currentPage } = getPaginationMeta(this.root);
        if (totalPages <= 1) {
          this.preloadDone = true;
          return;
        }

        const existingKeys = new Set(this.index.map((i) => i.key));

        for (let p = currentPage + 1; p <= totalPages; p++) {
          if (this.abortController.signal.aborted) return;
          if (CONFIG.maxItems > 0 && this.index.length >= CONFIG.maxItems) break;

          const lis = await this.fetchPageLis(p);
          if (!lis.length) continue;

          const frag = document.createDocumentFragment();
          let appended = 0;

          for (const li of lis) {
            const imported = document.importNode(li, true);
            const key = getKey(imported);
            if (key && existingKeys.has(key)) continue;
            if (key) existingKeys.add(key);
            frag.appendChild(imported);
            appended++;
          }

          if (appended && this.grid) {
            this.isInternalAppend = true;
            this.grid.appendChild(frag);
            this.isInternalAppend = false;

            this.buildIndex();
            this.applyFilter(this.input?.value || '');
          }
        }

        this.preloadDone = true;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.warn('[WithinSearch] preload error:', err);
      } finally {
        this.isPreloading = false;
        this.applyFilter(this.input?.value || '');
        if (!this.input?.value?.trim()) this.setStatus('');
      }
    }

    watchForDomReplacement() {
      this.moRebind?.disconnect();
      this.moRebind = new MutationObserver(() => {
        const newWrapper = this.root.querySelector(CONFIG.wrapperSelector);
        const newGrid = this.root.querySelector(CONFIG.gridSelector);
        if (!newWrapper || !newGrid) return;

        // If wrapper/grid changed identity, re-bind
        if (newWrapper !== this.wrapper || newGrid !== this.grid) {
          this.destroy(false);

          this.wrapper = null;
          this.grid = null;
          this.input = null;
          this.statusEl = null;

          this.bind();
        }
      });

      this.moRebind.observe(document.documentElement, { childList: true, subtree: true });
    }

    destroy(stopWatcher = true) {
      this.unbindInputListeners();
      this.disconnectGridObserver();

      if (stopWatcher && this.moRebind) this.moRebind.disconnect();
      if (stopWatcher) this.moRebind = null;

      if (this.abortController) this.abortController.abort();
      this.abortController = null;

      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      this._debounceTimer = null;

      // Show pagination again (safety)
      try { this.togglePaginationUI(false); } catch {}
    }
  }

  window.__KK_WITHIN_SEARCH__ = new WithinSearchController(document);
})();
